import { AppButton, AppSpin, AppTypography } from '@/components/atoms';
import BottomBar from '@/components/organisms/Footer';
import OrderSheetTable from '@/components/organisms/OrderSheetTable';
import {
  useOrderSheetForm,
  useOrderSheetInit,
  useOrderSheetSubmit,
} from '@/hooks/main/useOrderSheet';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import useAppModal from '@/hooks/useAppModal';
import { useTheme } from '@/shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CollapsibleCard } from '@/components/molecules';

export default function OrderSheetPage() {
  const { warning, confirm } = useAppModal();
  const { serviceType } = useTheme();
  const { tenant } = useClinicContext();
  const navigate = useNavigate();
  const { sheets, deliveryFeeMaster, isLoading: initLoading } = useOrderSheetInit();
  const { submitMutation, isLoading: submitLoading } = useOrderSheetSubmit();
  const {
    rowState,
    setRowState,
    handlers,
    totals,
    buildPayload,
    validateForSubmit,
    buildInitialRowState,
  } = useOrderSheetForm(sheets, deliveryFeeMaster);

  const handleSubmit = useCallback(() => {
    const validation = validateForSubmit();
    if (!validation.ok) {
      warning({ content: validation.message });
      return;
    }
    const payload = buildPayload();
    if (!payload) return;
    const confirmMsg = [
      'この内容で用紙発注してもよろしいですか？',
      `合計：￥${totals.subtotal.toLocaleString()}`,
      `送料：￥${totals.deliveryTotal.toLocaleString()}`,
      `合計金額：￥${totals.total.toLocaleString()}`,
    ].join('\n');
    confirm({
      content: <span className="whitespace-pre-line">{confirmMsg}</span>,
      focusable: { autoFocusButton: 'ok' },
      onOk: () =>
        submitMutation(payload).then(() => {
          setRowState({});
          navigate(`/${tenant}/sheets/histories`);
        }),
    });
  }, [
    validateForSubmit,
    buildPayload,
    totals,
    warning,
    confirm,
    submitMutation,
    setRowState,
    navigate,
    tenant,
  ]);

  if (initLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <AppSpin size="large" />
      </div>
    );
  }

  return (
    <div>
      <CollapsibleCard>
        <div className="mb-10">
          <AppTypography.H4 className="mb-4 text-2xl">用紙発注</AppTypography.H4>
          <AppTypography.Small className="block mb-4 text-xl">
            注文する用紙にチェックをつけて注文数を指定してください。
          </AppTypography.Small>
          <AppTypography.Small className="block text-xl">
            17：00以降と週末・<span className="text-red-500">祝日</span>
            の発注は、翌営業日の発送となります。
          </AppTypography.Small>
          {serviceType === SERVICE_TYPE_R2 && (
            <AppTypography.Small className="block text-xl">
              ※遠方の場合は、お届けまで日数がかかりますのでご注意下さい。
            </AppTypography.Small>
          )}
        </div>
        <div>
          <OrderSheetTable
            sheets={sheets}
            rowState={rowState}
            deliveryFeeMaster={deliveryFeeMaster}
            handlers={handlers}
            subtotal={totals.subtotal}
            buildInitialRowState={buildInitialRowState}
          />
        </div>
      </CollapsibleCard>

      <BottomBar
        variant="action"
        visible
        right={
          <AppButton
            type="primary"
            className="rounded"
            onClick={handleSubmit}
            loading={submitLoading}
          >
            <i className="mr-2 fa fa-send" aria-hidden="true" />
            発注
          </AppButton>
        }
        fixed
      />
    </div>
  );
}
